// src/pages/Opdrachtvoortgang.js
// vOVG-006
// - Algemeen raadpleegscherm voor het volledige orderproces
// - Offerte, opdracht, uitvoering, werkbon, oplevering, factuur en betaling
// - Herkenbaar voor project-/vloerbedrijven, maar algemeen toepasbaar
// - Layout gelijk aan WerkbonnenOverzicht en Organisaties

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useVoiceCommands from "../components/useVoiceCommands";
import HelpButton from "../components/HelpButton";

const VERSION = "vOVG-006";
const EMPTY_VAL = "__EMPTY__";

function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function safeTrim(value) {
  return safeText(value).trim();
}

function norm(value) {
  return safeText(value)
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function uniqSorted(values) {
  return Array.from(
    new Set((values || []).map((value) => safeTrim(value)).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b, "nl"));
}

function isoDate(value) {
  const text = safeText(value).slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : "";
}

function nlDate(value) {
  const date = isoDate(value);
  if (!date) return "";
  const [year, month, day] = date.split("-");
  return `${day}-${month}-${year}`;
}

function euro(value) {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "€ 0,00";

  return number.toLocaleString("nl-NL", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function buildHeaders() {
  let user = {};

  try {
    user = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const headers = {
    "Content-Type": "application/json",
    "x-tenant":
      sessionStorage.getItem("tenant") ||
      localStorage.getItem("tenant") ||
      "demo",
    "x-role": user?.role || "user",
    "x-staffiq-user-id": user?.id || "",
    "x-staffiq-user-email": user?.email || "",
    "x-staffiq-user-name": user?.display_name || user?.name || "",
    "x-company-code-ids":
      sessionStorage.getItem("staffiq_company_code_ids") ||
      localStorage.getItem("staffiq_company_code_ids") ||
      "",
  };

  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  if (token) headers.Authorization = `Bearer ${token}`;

  Object.keys(headers).forEach((key) => {
    if (!headers[key]) delete headers[key];
  });

  return headers;
}

async function fetchJson(url) {
  const response = await fetch(url, {
    headers: buildHeaders(),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(data?.error || `${response.status} ${response.statusText}`);
  }

  return data;
}

function offerStatusLabel(value) {
  const status = safeText(value).trim().toUpperCase();

  if (status === "FACTUUR") return "Factuur";
  if (status === "OPDRACHT") return "Opdracht";
  return "Offerte";
}

function approvalLabel(value) {
  const status = safeText(value).trim().toLowerCase();

  if (status === "approved") return "Akkoord";
  if (status === "rejected") return "Niet akkoord";
  if (status === "viewed") return "Bekeken";
  if (status === "sent") return "Verstuurd";
  if (status === "expired") return "Verlopen";
  if (status === "concept") return "Concept";
  return "";
}

function workOrderLabel(item) {
  if (!item?.work_order_id) return "Niet gemaakt";

  const status = safeText(item.work_order_status).trim().toUpperCase();
  const approval = safeText(item.work_approval_status).trim().toLowerCase();

  if (status === "COMPLETED") return "Opgeleverd";
  if (approval === "approved") return "Geaccordeerd";
  if (status === "PENDING_APPROVAL") return "Ter akkoord";
  return "Aangemaakt";
}

function invoiceLabel(item) {
  if (!item?.invoice_id) return "Niet gemaakt";

  const status = safeText(item.invoice_status).trim().toUpperCase();

  if (status === "PAID") return "Betaald";
  if (status === "PARTIAL") return "Deelbetaling";
  if (status === "FINAL") return "Definitief";
  return "Concept";
}

function processLabel(value) {
  const status = safeText(value).trim().toUpperCase();

  if (status === "COMPLETED") return "Afgerond";
  if (status === "ACTION_REQUIRED") return "Actie nodig";
  return "Lopend";
}

function processStyle(value) {
  const status = safeText(value).trim().toUpperCase();

  if (status === "COMPLETED") {
    return { background: "#e8f5e9", color: "#1b5e20", border: "#a5d6a7" };
  }

  if (status === "ACTION_REQUIRED") {
    return { background: "#fdecec", color: "#9b1c1c", border: "#efb1b1" };
  }

  return { background: "#eaf2f8", color: "#1f4e79", border: "#b8d2e8" };
}


function orderProgressLabel(item) {
  const approval = approvalLabel(item?.order_approval_status);
  if (approval) return approval;

  const offerStatus = safeText(item?.offer_status).trim().toUpperCase();

  // Zodra "Opdracht goedgekeurd" is uitgevoerd, staat de hoofdstatus op FACTUUR.
  // Daarmee is de opdrachtfase intern akkoord, ook als er geen apart digitaal
  // akkoordrecord voor de opdracht aanwezig is.
  if (offerStatus === "FACTUUR") return "Akkoord";

  return "";
}

function paymentProgressLabel(item) {
  if (!item?.invoice_id) return "";

  const invoiceStatus = safeText(item?.invoice_status).trim().toUpperCase();
  const outstanding = Number(item?.outstanding_amount || 0);
  const paid = Number(item?.paid_total || 0);
  const total = Number(item?.invoice_total || 0);

  // Een conceptfactuur is nooit betaald, ook niet wanneer totalen nog 0 zijn.
  if (invoiceStatus === "DRAFT" || invoiceStatus === "CONCEPT") return "";

  if (invoiceStatus === "PAID" || invoiceStatus === "BETAALD") return "Betaald";

  if (invoiceStatus === "PARTIAL" || paid > 0) {
    return outstanding <= 0.005 && total > 0 ? "Betaald" : "Deelbetaling";
  }

  if (invoiceStatus === "FINAL" || invoiceStatus === "GEFACTUREERD") {
    return outstanding <= 0.005 && total > 0 ? "Betaald" : "Open";
  }

  return "";
}

function effectiveProcessStatus(item) {
  const invoiceStatus = safeText(item?.invoice_status).trim().toUpperCase();
  const outstanding = Number(item?.outstanding_amount || 0);
  const total = Number(item?.invoice_total || 0);

  // Een conceptfactuur betekent dat het proces nog loopt.
  if (invoiceStatus === "DRAFT" || invoiceStatus === "CONCEPT") {
    return "RUNNING";
  }

  if (
    (invoiceStatus === "PAID" || invoiceStatus === "BETAALD") &&
    total > 0
  ) {
    return "COMPLETED";
  }

  if (
    ["FINAL", "GEFACTUREERD", "PARTIAL", "DEELBETALING"].includes(invoiceStatus) &&
    total > 0 &&
    outstanding <= 0.005
  ) {
    return "COMPLETED";
  }

  return safeText(item?.process_status || "RUNNING").trim().toUpperCase() || "RUNNING";
}

export default function Opdrachtvoortgang() {
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const pageTopRef = useRef(null);
  const midScrollRef = useRef(null);
  const rowRefs = useRef([]);

  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [listIndex, setListIndex] = useState(-1);

  const [q, setQ] = useState("");
  const [organisationFilterId, setOrganisationFilterId] = useState(getParam("organisation_id"));
  const [topStatus, setTopStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [offerNumberFilter, setOfferNumberFilter] = useState("");
  const [relationFilter, setRelationFilter] = useState("");
  const [offerDateFilter, setOfferDateFilter] = useState("");
  const [offerFilter, setOfferFilter] = useState("");
  const [orderFilter, setOrderFilter] = useState("");
  const [executionFilter, setExecutionFilter] = useState("");
  const [workOrderFilter, setWorkOrderFilter] = useState("");
  const [invoiceFilter, setInvoiceFilter] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");
  const [processFilter, setProcessFilter] = useState("");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");

  const [sort, setSort] = useState({
    key: "offer_date",
    dir: "desc",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [flash, setFlash] = useState("");
  const [speechInfo, setSpeechInfo] = useState("");
  const [hideVoiceError, setHideVoiceError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  async function loadItems() {
    try {
      setLoading(true);
      setError("");

      const data = await fetchJson("/api/order-process-overview");
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || "Opdrachtvoortgang laden mislukt.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  useEffect(() => {
    function handleScopeChange() {
      setReloadKey((value) => value + 1);
    }

    window.addEventListener(
      "staffiq-company-scope-changed",
      handleScopeChange
    );

    return () => {
      window.removeEventListener(
        "staffiq-company-scope-changed",
        handleScopeChange
      );
    };
  }, []);

  const derived = useMemo(() => {
    return items.map((item) => {
      const processStatus = effectiveProcessStatus(item);

      return {
        ...item,
        offer_phase: offerStatusLabel(item.offer_status),
        offer_approval_label: approvalLabel(item.offer_approval_status),
        order_approval_label: orderProgressLabel(item),
        execution_label: item.execution_date
          ? `${nlDate(item.execution_date)}${item.start_time ? ` ${item.start_time}` : ""}`
          : "",
        work_order_label: workOrderLabel(item),
        invoice_label: invoiceLabel(item),
        payment_label: paymentProgressLabel(item),
        process_status: processStatus,
        process_label: processLabel(processStatus),
      };
    });
  }, [items]);

  const options = useMemo(() => ({
    offerNumber: uniqSorted(derived.map((item) => item.offer_number)),
    relation: uniqSorted(derived.map((item) => item.organisation_name)),
    offerDate: uniqSorted(derived.map((item) => isoDate(item.offer_date))),
    offer: uniqSorted(derived.map((item) => item.offer_phase)),
    order: uniqSorted(derived.map((item) => item.order_approval_label)),
    execution: uniqSorted(derived.map((item) => isoDate(item.execution_date))),
    workOrder: uniqSorted(derived.map((item) => item.work_order_label)),
    invoice: uniqSorted(derived.map((item) => item.invoice_label)),
    payment: uniqSorted(derived.map((item) => item.payment_label)),
    process: uniqSorted(derived.map((item) => item.process_label)),
    companyCode: uniqSorted(
      derived.map((item) => item.company_code_name || item.company_code)
    ),
  }), [derived]);

  const filteredItems = useMemo(() => {
    const query = norm(q);

    function matches(value, filter) {
      const clean = safeTrim(value);

      if (!filter) return true;
      if (filter === EMPTY_VAL) return !clean;

      return clean === filter;
    }

    return derived.filter((item) => {
      const offerDate = isoDate(item.offer_date);

      if (
        organisationFilterId &&
        String(item.organisation_id || "") !== String(organisationFilterId)
      ) {
        return false;
      }

      if (query) {
        const haystack = norm([
          item.offer_number,
          item.organisation_name,
          item.contact_name,
          item.reference,
          item.offer_phase,
          item.offer_approval_label,
          item.order_approval_label,
          item.executed_by,
          item.work_order_label,
          item.invoice_number,
          item.invoice_label,
          item.payment_label,
          item.process_label,
          item.company_code_name,
          item.company_code,
        ].join(" "));

        if (!haystack.includes(query)) return false;
      }

      if (topStatus && safeText(item.process_status).toUpperCase() !== topStatus) {
        return false;
      }

      if (dateFrom && (!offerDate || offerDate < dateFrom)) return false;
      if (dateTo && (!offerDate || offerDate > dateTo)) return false;

      if (!matches(item.offer_number, offerNumberFilter)) return false;
      if (!matches(item.organisation_name, relationFilter)) return false;
      if (!matches(offerDate, offerDateFilter)) return false;
      if (!matches(item.offer_phase, offerFilter)) return false;
      if (!matches(item.order_approval_label, orderFilter)) return false;
      if (!matches(isoDate(item.execution_date), executionFilter)) return false;
      if (!matches(item.work_order_label, workOrderFilter)) return false;
      if (!matches(item.invoice_label, invoiceFilter)) return false;
      if (!matches(item.payment_label, paymentFilter)) return false;
      if (!matches(item.process_label, processFilter)) return false;
      if (!matches(item.company_code_name || item.company_code, companyCodeFilter)) {
        return false;
      }

      return true;
    });
  }, [
    derived,
    q,
    topStatus,
    dateFrom,
    dateTo,
    offerNumberFilter,
    relationFilter,
    offerDateFilter,
    offerFilter,
    orderFilter,
    executionFilter,
    workOrderFilter,
    invoiceFilter,
    paymentFilter,
    processFilter,
    companyCodeFilter,
    organisationFilterId,
  ]);

  const sortedItems = useMemo(() => {
    const rows = [...filteredItems];
    const direction = sort.dir === "desc" ? -1 : 1;

    rows.sort((a, b) => {
      let av = safeText(a?.[sort.key]).toLowerCase();
      let bv = safeText(b?.[sort.key]).toLowerCase();

      if (
        sort.key === "offer_date" ||
        sort.key === "execution_date" ||
        sort.key === "invoice_date"
      ) {
        av = isoDate(av);
        bv = isoDate(bv);
      }

      if (sort.key === "order_total" || sort.key === "outstanding_amount") {
        av = Number(a?.[sort.key] || 0);
        bv = Number(b?.[sort.key] || 0);
      }

      if (av === bv) return 0;
      return av < bv ? -1 * direction : 1 * direction;
    });

    return rows;
  }, [filteredItems, sort]);

  const selected =
    derived.find((item) => String(item.offer_id) === String(selectedId)) || null;

  const counts = useMemo(() => ({
    running: derived.filter(
      (item) => safeText(item.process_status).toUpperCase() === "RUNNING"
    ).length,
    action: derived.filter(
      (item) => safeText(item.process_status).toUpperCase() === "ACTION_REQUIRED"
    ).length,
    completed: derived.filter(
      (item) => safeText(item.process_status).toUpperCase() === "COMPLETED"
    ).length,
  }), [derived]);

  const gridCount = Math.min(sortedItems.length, 9999);

  function sortToggle(key) {
    setSort((previous) => {
      if (previous.key !== key) return { key, dir: "asc" };
      return {
        key,
        dir: previous.dir === "asc" ? "desc" : "asc",
      };
    });
  }

  function sortArrow(key) {
    if (sort.key !== key) return "";
    return sort.dir === "asc" ? " ▲" : " ▼";
  }

  function resetColumnFilters() {
    setOfferNumberFilter("");
    setRelationFilter("");
    setOfferDateFilter("");
    setOfferFilter("");
    setOrderFilter("");
    setExecutionFilter("");
    setWorkOrderFilter("");
    setInvoiceFilter("");
    setPaymentFilter("");
    setProcessFilter("");
    setCompanyCodeFilter("");
  }

  function resetFilters() {
    setQ("");
    setOrganisationFilterId("");
    setTopStatus("");
    setDateFrom("");
    setDateTo("");
    resetColumnFilters();
    setSelectedId("");
    setListIndex(-1);
    setSort({ key: "offer_date", dir: "desc" });
    setHideVoiceError(true);
    setReloadKey((value) => value + 1);

    setFlash("Vernieuwd ✅");
    setTimeout(() => setFlash(""), 800);

    requestAnimationFrame(() => {
      try {
        midScrollRef.current?.scrollTo?.({
          top: 0,
          left: 0,
          behavior: "auto",
        });
      } catch {}

      try {
        pageTopRef.current?.scrollIntoView?.({
          behavior: "auto",
          block: "start",
        });
      } catch {}

      if (window.innerWidth > 768) {
        try {
          searchRef.current?.focus?.({ preventScroll: true });
          searchRef.current?.select?.();
        } catch {
          searchRef.current?.focus?.();
        }
      }
    });
  }

  function openOrder(item) {
    if (!item?.offer_id) return;

    try {
      sessionStorage.setItem(
        "staffiq_reopen_offer_id",
        String(item.offer_id)
      );
    } catch {}

    navigate("/offertes");
  }

  function openWorkOrder(item) {
    if (!item?.offer_id || !item?.work_order_id) return;
    navigate(`/werkbon-oplevering/${item.offer_id}`);
  }

  function openInvoice(item) {
    if (!item?.invoice_id) return;

    try {
      sessionStorage.setItem(
        "staffiq_reopen_invoice_id",
        String(item.invoice_id)
      );
    } catch {}

    navigate("/facturen");
  }

  function moveCursor(delta) {
    setListIndex((previous) => {
      if (!sortedItems.length) return -1;

      const current = previous < 0 ? 0 : previous;
      const next = Math.max(
        0,
        Math.min(sortedItems.length - 1, current + delta)
      );

      setTimeout(() => {
        rowRefs.current?.[next]?.scrollIntoView?.({
          block: "nearest",
        });
      }, 0);

      return next;
    });
  }

  function selectCurrentRow() {
    if (!sortedItems.length) return;

    const index =
      listIndex >= 0 && listIndex < sortedItems.length
        ? listIndex
        : 0;

    const row = sortedItems[index];
    if (!row) return;

    setListIndex(index);
    setSelectedId(String(row.offer_id));
  }

  function handleVoiceCommand(raw) {
    const spoken = norm(raw);

    if (spoken === "volgende") {
      moveCursor(1);
      return true;
    }

    if (spoken === "vorige") {
      moveCursor(-1);
      return true;
    }

    if (
      spoken === "selecteer" ||
      spoken === "selecteren" ||
      spoken === "rechts"
    ) {
      selectCurrentRow();
      return true;
    }

    if (spoken === "vernieuw" || spoken === "vernieuwen") {
      resetFilters();
      return true;
    }

    if (spoken === "lopend") {
      setTopStatus("RUNNING");
      return true;
    }

    if (spoken === "actie nodig") {
      setTopStatus("ACTION_REQUIRED");
      return true;
    }

    if (spoken === "afgerond") {
      setTopStatus("COMPLETED");
      return true;
    }

    if (spoken === "alles" || spoken === "alle statussen") {
      setTopStatus("");
      return true;
    }

    return false;
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 6500,

    onCommand: (_command, rawHeard) => {
      const raw = safeTrim(rawHeard);

      if (raw) {
        setSpeechInfo(`🎙 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }

      return raw ? handleVoiceCommand(raw) : false;
    },

    onDictation: (spokenText) => {
      const raw = safeTrim(spokenText);
      if (!raw) return;

      setSpeechInfo(`🎤 ${raw}`);
      setTimeout(() => setSpeechInfo(""), 2000);

      if (handleVoiceCommand(raw)) return;

      setQ(raw);
      setListIndex(0);
    },
  });

  function startSpeak() {
    if (!voice.supported || voice.listening) return;
    voice.startOnce();
  }

  const isMobile = window.innerWidth <= 768;
  const TABLE_MIN_WIDTH = 1420;

  const searchInputStyle = {
    padding: "7px 10px",
    width: 180,
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    outline: "none",
    boxSizing: "border-box",
  };

  const topSelectStyle = {
    height: 32,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const topDateStyle = {
    height: 32,
    width: 138,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    padding: "7px 12px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };

  const buttonDisabledStyle = {
    ...buttonStyle,
    opacity: 0.55,
    cursor: "not-allowed",
  };

  const countStyle = {
    minWidth: 52,
    height: 32,
    padding: "0 10px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "#f7fafc",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    color: "#1f4e79",
    boxSizing: "border-box",
    whiteSpace: "nowrap",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
    tableLayout: "fixed",
  };

  const thBase = {
    textAlign: "left",
    fontSize: 12,
    color: "#444",
    fontWeight: 700,
    padding: "8px 6px",
    borderBottom: "1px solid #e6e6e6",
    whiteSpace: "nowrap",
    position: "sticky",
    top: 0,
    zIndex: 5,
    background: "#fff",
    userSelect: "none",
  };

  const thClickable = {
    ...thBase,
    cursor: "pointer",
  };

  const filterThStyle = {
    ...thBase,
    fontWeight: 400,
    padding: "6px 6px 8px 6px",
    borderBottom: "1px solid #ededed",
    top: 32,
    zIndex: 4,
    cursor: "default",
  };

  const selectStyle = {
    height: 30,
    padding: "0 8px",
    border: "1px solid #d9d9d9",
    borderRadius: 4,
    background: "white",
    width: "100%",
    boxSizing: "border-box",
  };

  const tdStyle = {
    padding: "7px 6px",
    borderBottom: "1px solid #efefef",
    fontSize: 13,
    color: "#222",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle",
  };

  const colWidths = {
    offerNumber: 105,
    relation: 145,
    offerDate: 95,
    offer: 95,
    order: 105,
    execution: 115,
    workOrder: 115,
    invoice: 105,
    payment: 105,
    outstanding: 100,
    process: 105,
    companyCode: 105,
  };

  function StatusButton({ statusKey, label, count }) {
    const active = topStatus === statusKey;
    const badge = processStyle(statusKey);

    return (
      <button
        type="button"
        onClick={() =>
          setTopStatus((previous) =>
            previous === statusKey ? "" : statusKey
          )
        }
        style={{
          ...buttonStyle,
          background: active ? badge.background : "white",
          color: active ? badge.color : "#333",
          border: `1px solid ${active ? badge.border : "#d9d9d9"}`,
          fontWeight: active ? 700 : 400,
        }}
      >
        {label}: <b>{count}</b>
      </button>
    );
  }

  function FilterSelect({
    value,
    onChange,
    options,
    hasEmpty = false,
    formatOption = (option) => option,
  }) {
    return (
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={selectStyle}
      >
        <option value="">(alle)</option>
        {hasEmpty && <option value={EMPTY_VAL}>(leeg)</option>}
        {options.map((option) => (
          <option key={option} value={option}>
            {formatOption(option)}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div
        ref={pageTopRef}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginTop: 0,
          marginBottom: 10,
        }}
      >
        <h1 style={{ margin: 0 }}>
          Opdrachtvoortgang{" "}
          <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
        </h1>

        <HelpButton
          topic="opdrachtvoortgang"
          title="Help over Opdrachtvoortgang"
        />
      </div>

      {!isMobile ? (
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "nowrap",
          }}
        >
          <input
            ref={searchRef}
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Zoek…"
            style={searchInputStyle}
          />

          <div
            title={`Aantal regels in het grid: ${gridCount}`}
            style={countStyle}
          >
            {gridCount}
          </div>

          <select
            value={topStatus}
            onChange={(event) => setTopStatus(event.target.value)}
            style={{ ...topSelectStyle, width: 140 }}
            title="Status"
          >
            <option value="">Alle statussen</option>
            <option value="RUNNING">Lopend</option>
            <option value="ACTION_REQUIRED">Actie nodig</option>
            <option value="COMPLETED">Afgerond</option>
          </select>

          <input
            type="date"
            value={dateFrom}
            onChange={(event) => setDateFrom(event.target.value)}
            style={topDateStyle}
            title="Vanaf offertedatum"
          />

          <input
            type="date"
            value={dateTo}
            onChange={(event) => setDateTo(event.target.value)}
            style={topDateStyle}
            title="Tot en met offertedatum"
          />

          <button
            onMouseDown={(event) => event.preventDefault()}
            onTouchStart={(event) => event.preventDefault()}
            onClick={startSpeak}
            disabled={!voice.supported || voice.listening}
            style={
              !voice.supported || voice.listening
                ? buttonDisabledStyle
                : buttonStyle
            }
          >
            {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
          </button>

          <button type="button" onClick={resetFilters} style={buttonStyle}>
            🔄 Vernieuw
          </button>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) 58px",
              gap: 6,
              marginBottom: 8,
            }}
          >
            <input
              ref={searchRef}
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder="Zoek…"
              style={{
                ...searchInputStyle,
                width: "100%",
                minWidth: 0,
              }}
            />

            <div style={{ ...countStyle, width: "100%", minWidth: 0 }}>
              {gridCount}
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 6,
              marginBottom: 6,
            }}
          >
            <select
              value={topStatus}
              onChange={(event) => setTopStatus(event.target.value)}
              style={{ ...topSelectStyle, width: "100%" }}
            >
              <option value="">Alle statussen</option>
              <option value="RUNNING">Lopend</option>
              <option value="ACTION_REQUIRED">Actie nodig</option>
              <option value="COMPLETED">Afgerond</option>
            </select>

            <button
              onClick={startSpeak}
              disabled={!voice.supported || voice.listening}
              style={
                !voice.supported || voice.listening
                  ? buttonDisabledStyle
                  : buttonStyle
              }
            >
              {voice.listening ? "🎤..." : "🎤 Spreek"}
            </button>

            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              style={{ ...topDateStyle, width: "100%" }}
            />

            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              style={{ ...topDateStyle, width: "100%" }}
            />

            <button
              type="button"
              onClick={resetFilters}
              style={{ ...buttonStyle, gridColumn: "1 / -1" }}
            >
              🔄 Vernieuw
            </button>
          </div>
        </>
      )}

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 10,
          marginBottom: 10,
        }}
      >
        <StatusButton
          statusKey="RUNNING"
          label="Lopend"
          count={counts.running}
        />

        <StatusButton
          statusKey="ACTION_REQUIRED"
          label="Actie nodig"
          count={counts.action}
        />

        <StatusButton
          statusKey="COMPLETED"
          label="Afgerond"
          count={counts.completed}
        />
      </div>

      <div style={{ minHeight: 20 }}>
        {loading && <div>Laden…</div>}
        {error && <div style={{ color: "crimson" }}>{error}</div>}
        {flash && <div style={{ color: "green" }}>{flash}</div>}
        {speechInfo && <div style={{ color: "#1f4e79" }}>{speechInfo}</div>}
        {voice.error && !hideVoiceError && (
          <div style={{ color: "crimson" }}>
            Spraakfout: {voice.error}
          </div>
        )}
      </div>

      {!loading && !error && (
        <div style={{ marginTop: 4, minWidth: 0 }}>
          <div
            ref={midScrollRef}
            style={{
              overflowX: "scroll",
              overflowY: "auto",
              maxHeight: "calc(100vh - 300px)",
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              background: "#fff",
            }}
          >
            <div style={{ minWidth: TABLE_MIN_WIDTH }}>
              <table style={tableStyle}>
                <colgroup>
                  <col style={{ width: colWidths.offerNumber }} />
                  <col style={{ width: colWidths.relation }} />
                  <col style={{ width: colWidths.offerDate }} />
                  <col style={{ width: colWidths.offer }} />
                  <col style={{ width: colWidths.order }} />
                  <col style={{ width: colWidths.execution }} />
                  <col style={{ width: colWidths.workOrder }} />
                  <col style={{ width: colWidths.invoice }} />
                  <col style={{ width: colWidths.payment }} />
                  <col style={{ width: colWidths.outstanding }} />
                  <col style={{ width: colWidths.process }} />
                  <col style={{ width: colWidths.companyCode }} />
                </colgroup>

                <thead>
                  <tr>
                    <th style={thClickable} onClick={() => sortToggle("offer_number")}>
                      Referentie{sortArrow("offer_number")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("organisation_name")}>
                      Relatie{sortArrow("organisation_name")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("offer_date")}>
                      Offertedatum{sortArrow("offer_date")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("offer_phase")}>
                      Offerte{sortArrow("offer_phase")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("order_approval_label")}>
                      Opdracht{sortArrow("order_approval_label")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("execution_date")}>
                      Uitvoering{sortArrow("execution_date")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("work_order_label")}>
                      Werkbon{sortArrow("work_order_label")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("invoice_label")}>
                      Factuur{sortArrow("invoice_label")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("payment_label")}>
                      Betaling{sortArrow("payment_label")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("outstanding_amount")}>
                      Openstaand{sortArrow("outstanding_amount")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("process_label")}>
                      Proces{sortArrow("process_label")}
                    </th>
                    <th style={thClickable} onClick={() => sortToggle("company_code_name")}>
                      Bedrijfscode{sortArrow("company_code_name")}
                    </th>
                  </tr>

                  <tr>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={offerNumberFilter}
                        onChange={setOfferNumberFilter}
                        options={options.offerNumber}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={relationFilter}
                        onChange={setRelationFilter}
                        options={options.relation}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={offerDateFilter}
                        onChange={setOfferDateFilter}
                        options={options.offerDate}
                        hasEmpty={derived.some((item) => !isoDate(item.offer_date))}
                        formatOption={nlDate}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={offerFilter}
                        onChange={setOfferFilter}
                        options={options.offer}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={orderFilter}
                        onChange={setOrderFilter}
                        options={options.order}
                        hasEmpty={derived.some((item) => !safeTrim(item.order_approval_label))}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={executionFilter}
                        onChange={setExecutionFilter}
                        options={options.execution}
                        hasEmpty={derived.some((item) => !isoDate(item.execution_date))}
                        formatOption={nlDate}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={workOrderFilter}
                        onChange={setWorkOrderFilter}
                        options={options.workOrder}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={invoiceFilter}
                        onChange={setInvoiceFilter}
                        options={options.invoice}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={paymentFilter}
                        onChange={setPaymentFilter}
                        options={options.payment}
                        hasEmpty={derived.some((item) => !safeTrim(item.payment_label))}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <select style={selectStyle} disabled>
                        <option>(bedrag)</option>
                      </select>
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={processFilter}
                        onChange={setProcessFilter}
                        options={options.process}
                      />
                    </th>
                    <th style={filterThStyle}>
                      <FilterSelect
                        value={companyCodeFilter}
                        onChange={setCompanyCodeFilter}
                        options={options.companyCode}
                      />
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sortedItems.map((item, index) => {
                    const isSelected =
                      String(item.offer_id) === String(selectedId);
                    const isCursor = index === listIndex;
                    const badge = processStyle(item.process_status);

                    let background = "transparent";
                    if (isSelected) background = "#e6f0ff";
                    else if (isCursor) background = "#f3f7ff";

                    return (
                      <tr
                        key={item.offer_id}
                        ref={(element) => {
                          rowRefs.current[index] = element;
                        }}
                        onClick={() => {
                          setSelectedId(String(item.offer_id));
                          setListIndex(index);
                        }}
                        onDoubleClick={() => openOrder(item)}
                        style={{
                          backgroundColor: background,
                          cursor: "pointer",
                        }}
                      >
                        <td style={tdStyle} title={item.offer_number || item.offer_id}>
                          <b>{item.offer_number || item.offer_id}</b>
                        </td>
                        <td style={tdStyle} title={item.organisation_name || "-"}>
                          {item.organisation_name || "-"}
                        </td>
                        <td style={tdStyle}>
                          {nlDate(item.offer_date) || "-"}
                        </td>
                        <td style={tdStyle} title={item.offer_approval_label || item.offer_phase}>
                          {item.offer_approval_label || item.offer_phase}
                        </td>
                        <td style={tdStyle} title={item.order_approval_label || "-"}>
                          {item.order_approval_label || "-"}
                        </td>
                        <td style={tdStyle} title={item.execution_label || "-"}>
                          {item.execution_label || "-"}
                        </td>
                        <td style={tdStyle} title={item.work_order_label}>
                          {item.work_order_label}
                        </td>
                        <td style={tdStyle} title={item.invoice_number || item.invoice_label}>
                          {item.invoice_number
                            ? `${item.invoice_number} · ${item.invoice_label}`
                            : item.invoice_label}
                        </td>
                        <td style={tdStyle}>
                          {item.payment_label || "-"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {item.invoice_id ? euro(item.outstanding_amount) : "-"}
                        </td>
                        <td style={tdStyle}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "3px 7px",
                              borderRadius: 999,
                              background: badge.background,
                              color: badge.color,
                              border: `1px solid ${badge.border}`,
                              whiteSpace: "nowrap",
                              fontWeight: 700,
                            }}
                          >
                            {item.process_label}
                          </span>
                        </td>
                        <td style={tdStyle}>
                          {item.company_code_name || item.company_code || "-"}
                        </td>
                      </tr>
                    );
                  })}

                  {!sortedItems.length && (
                    <tr>
                      <td colSpan="12" style={{ padding: 12, opacity: 0.7 }}>
                        Geen resultaten (filters te streng?)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          marginTop: 14,
          padding: 14,
          background: "white",
          border: "1px solid #dce2e8",
          borderRadius: 8,
          minHeight: 105,
        }}
      >
        {selected ? (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              alignItems: "start",
            }}
          >
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 4 }}>
                {selected.offer_number || selected.offer_id} —{" "}
                {selected.organisation_name}
              </div>

              <div style={{ fontSize: 13, lineHeight: 1.55 }}>
                Offerte: {selected.offer_phase}
                {selected.offer_approval_label
                  ? ` · ${selected.offer_approval_label}`
                  : ""}
                <br />
                Opdracht: {selected.order_approval_label || "-"}
                <br />
                Uitvoering: {selected.execution_label || "-"}
                <br />
                Werkbon/Oplevering: {selected.work_order_label}
                <br />
                Factuur:{" "}
                {selected.invoice_number
                  ? `${selected.invoice_number} · ${selected.invoice_label}`
                  : selected.invoice_label}
                <br />
                Betaling: {selected.payment_label || "-"}
                {selected.invoice_id
                  ? ` · Openstaand ${euro(selected.outstanding_amount)}`
                  : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={() => openOrder(selected)}
                style={buttonStyle}
              >
                Open opdracht
              </button>

              <button
                type="button"
                onClick={() => openWorkOrder(selected)}
                disabled={!selected.work_order_id}
                style={
                  selected.work_order_id
                    ? buttonStyle
                    : buttonDisabledStyle
                }
              >
                Open werkbon
              </button>

              <button
                type="button"
                onClick={() => openInvoice(selected)}
                disabled={!selected.invoice_id}
                style={
                  selected.invoice_id
                    ? buttonStyle
                    : buttonDisabledStyle
                }
              >
                Open factuur
              </button>
            </div>
          </div>
        ) : (
          <div style={{ color: "#777" }}>
            Selecteer een procesregel voor de details en knoppen.
          </div>
        )}
      </div>
    </div>
  );
}
